import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Check, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const MODELS = [
  { key: "scrum", name: "Scrum", desc: "Fixed sprints, locked scope, structured ceremonies" },
  { key: "hybrid", name: "Hybrid", desc: "Sprint cadence with continuous-flow flexibility" },
  { key: "lean", name: "Lean", desc: "Continuous Kanban flow, no fixed sprints" },
];

export default function NewProject() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useApp();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ type: "internal", operating_model: "scrum", sprint_length: 2, docs_links: [] });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: pipelines = [] } = useQuery({ queryKey: ["pm-pipelines"], queryFn: () => get("/pm/pipelines") });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => get("/crm/clients"), enabled: can("crm", "view") });

  const create = useMutation({
    mutationFn: (body) => post("/pm/projects", body),
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ["projects"] }); toast.success("Project submitted for approval"); navigate(`/pm/projects/${p.id}`); },
  });

  const steps = ["Basics", "Operating Model", "Budget & Links", "Board", "Review"];
  const defaultPipeline = pipelines.find((p) => p.is_default) || pipelines[0];
  const selectedPipeline = pipelines.find((p) => p.id === form.pipeline_id) || defaultPipeline;

  const addDocLink = () => set("docs_links", [...(form.docs_links || []), { label: "", url: "" }]);
  const setDocLink = (i, key, val) => set("docs_links", form.docs_links.map((d, idx) => idx === i ? { ...d, [key]: val } : d));
  const removeDocLink = (i) => set("docs_links", form.docs_links.filter((_, idx) => idx !== i));

  return (
    <div className="max-w-2xl">
      <PageHeader title="New Project" subtitle="Set up a delivery or internal project." />
      <div className="mb-6 flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <div className={cn("flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold",
              step > i + 1 ? "bg-emerald-500 text-white" : step === i + 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
              {step > i + 1 ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span className={cn("text-xs", step === i + 1 ? "font-semibold text-foreground" : "text-muted-foreground")}>{s}</span>
            {i < steps.length - 1 && <div className="h-px flex-1 bg-border" />}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        {step === 1 && (
          <div className="space-y-4">
            <div><Label>Project Name *</Label><Input data-testid="np-name" className="mt-1" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
            <div>
              <Label>Project Type</Label>
              <div className="mt-2 flex gap-2">
                {["client_delivery", "internal", "rnd"].map((t) => (
                  <button key={t} data-testid={`np-type-${t}`} onClick={() => set("type", t)}
                    className={cn("rounded-lg border px-3 py-1.5 text-xs font-medium capitalize", form.type === t ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground")}>
                    {t.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            {can("crm", "view") && (
              <div>
                <Label>Client</Label>
                <Select value={form.client_id || "none"} onValueChange={(v) => set("client_id", v === "none" ? null : v)}>
                  <SelectTrigger className="mt-1" data-testid="np-client"><SelectValue placeholder="Optional — leave unset for internal projects" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">Only visible to CRM-permitted users — the project name is what everyone else sees.</p>
              </div>
            )}
            <div><Label>Description</Label><Textarea className="mt-1" value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3">
            {MODELS.map((m) => (
              <button key={m.key} data-testid={`np-model-${m.key}`} onClick={() => set("operating_model", m.key)}
                className={cn("flex w-full items-center gap-3 rounded-xl border-2 p-4 text-left", form.operating_model === m.key ? "border-primary bg-primary/5" : "border-border hover:bg-muted")}>
                <div className="flex-1"><div className="font-display font-semibold">{m.name}</div><div className="text-xs text-muted-foreground">{m.desc}</div></div>
                {form.operating_model === m.key && <Check className="h-5 w-5 text-primary" />}
              </button>
            ))}
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <div><Label>Cost Center Name *</Label><Input data-testid="np-cc" className="mt-1" value={form.cost_center || form.name || ""} onChange={(e) => set("cost_center", e.target.value)} /></div>
            <div><Label>Budget Amount *</Label><Input data-testid="np-budget" type="number" className="mt-1" value={form.budget || ""} onChange={(e) => set("budget", Number(e.target.value))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Repo URL</Label><Input data-testid="np-repo" className="mt-1" placeholder="https://github.com/..." value={form.repo_url || ""} onChange={(e) => set("repo_url", e.target.value)} /></div>
              <div><Label>Deployed (Staging) Link</Label><Input data-testid="np-deployed" className="mt-1" placeholder="https://staging..." value={form.deployed_link || ""} onChange={(e) => set("deployed_link", e.target.value)} /></div>
              <div className="col-span-2"><Label>Production Link</Label><Input data-testid="np-prod" className="mt-1" placeholder="https://..." value={form.production_link || ""} onChange={(e) => set("production_link", e.target.value)} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between"><Label>Docs / Guidelines</Label>
                <button type="button" data-testid="np-add-doc" onClick={addDocLink} className="flex items-center gap-1 text-xs text-primary hover:underline"><Plus className="h-3.5 w-3.5" />Add link</button>
              </div>
              <div className="mt-2 space-y-2">
                {(form.docs_links || []).map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input placeholder="Label" className="w-32" value={d.label} onChange={(e) => setDocLink(i, "label", e.target.value)} />
                    <Input placeholder="URL" className="flex-1" value={d.url} onChange={(e) => setDocLink(i, "url", e.target.value)} />
                    <button type="button" onClick={() => removeDocLink(i)} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Choose which board template to start from — this sets the initial columns for this project's board.</p>
            {pipelines.map((p) => (
              <button key={p.id} data-testid={`np-pipeline-${p.id}`} onClick={() => set("pipeline_id", p.id)}
                className={cn("flex w-full items-center gap-3 rounded-xl border-2 p-4 text-left", (form.pipeline_id || defaultPipeline?.id) === p.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted")}>
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: p.color }} />
                <div className="flex-1">
                  <div className="font-display font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.columns.map((c) => c.name).join(" → ")}</div>
                </div>
                {(form.pipeline_id || defaultPipeline?.id) === p.id && <Check className="h-5 w-5 text-primary" />}
              </button>
            ))}
            <p className="text-xs text-muted-foreground">Need a different set of columns? Create or edit board templates under Project Management → Board Templates.</p>
          </div>
        )}
        {step === 5 && (
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Name</dt><dd className="font-medium">{form.name || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Type</dt><dd className="font-medium capitalize">{form.type?.replace("_", " ")}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Operating Model</dt><dd className="font-medium capitalize">{form.operating_model}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Budget</dt><dd className="font-medium">${(form.budget || 0).toLocaleString()}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Board Template</dt><dd className="font-medium">{selectedPipeline?.name || "—"}</dd></div>
          </dl>
        )}

        <div className="mt-6 flex justify-between">
          <Button variant="ghost" disabled={step === 1} onClick={() => setStep((s) => s - 1)}>← Back</Button>
          {step < 5
            ? <Button data-testid="np-next" onClick={() => { if (step === 1 && !form.name) return toast.error("Name required"); setStep((s) => s + 1); }}>Next</Button>
            : <Button data-testid="np-submit" onClick={() => create.mutate({ ...form, pipeline_id: form.pipeline_id || defaultPipeline?.id })} disabled={create.isPending}>Submit for Approval</Button>}
        </div>
      </div>
    </div>
  );
}
